import { useTranslation } from "react-i18next";
import { CloudUpload, Film } from "lucide-react";
import { FileDropzone, FileItem } from "../ui/FileDropzone";
import { FormField, FormItem } from "../ui/form";
import { TranslatedFormMessage } from "../ui/custom-form";
import { LoadImageDropzoneCta } from "./LoadImageDropzoneCta";
import { FormFieldProps } from "./types";
import { Thumbnail } from "../ui/tumbnail";
import { VIDEO_MAX_SIZE_MB } from "@/api/image";
import { isVideoFile, VIDEO_DROPZONE_ACCEPT } from "@/lib/media/video";

export const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

const IMAGE_ACCEPT: Record<string, string[]> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
};

export { isVideoFile };

interface DragAndDropLoadMediaProps extends FormFieldProps {
  /** Allow dropping videos in addition to images. Default true. */
  allowVideo?: boolean;
}

/**
 * Single drag-and-drop zone that accepts a photo OR a video (no tab/toggle).
 * The dropped File is stored in the RHF field (`config.name`); the type is
 * detected from the File itself (`file.type`) so the parent can branch its
 * upload/display logic. Set `allowVideo={false}` to restrict to images.
 */
const DragAndDropLoadMedia = ({
  fileItemValidationMark = false,
  control,
  config,
  className,
  content,
  allowVideo = true,
}: DragAndDropLoadMediaProps) => {
  const { t } = useTranslation();

  const accept = allowVideo
    ? { ...IMAGE_ACCEPT, ...VIDEO_DROPZONE_ACCEPT }
    : IMAGE_ACCEPT;

  const helper =
    content ??
    (allowVideo
      ? t("loadMedia.hint", { max: VIDEO_MAX_SIZE_MB })
      : undefined);

  return (
    <FormField
      control={control}
      name={config.name}
      render={({ field, fieldState }) => {
        const value: File | undefined = field.value;
        const video = isVideoFile(value);

        return value == undefined || fieldState.error ? (
          <FormItem>
            <FileDropzone
              multiple={false}
              accept={accept}
              onDrop={(f: File[]) => field.onChange(f[0])}
              className={className}
              children={() => (
                <div className="flex flex-col items-center justify-center gap-6">
                  <Thumbnail>
                    <CloudUpload className="text-gray-700 size-6" />
                  </Thumbnail>
                  <LoadImageDropzoneCta dragCtaKey="loadImage.dragCtaSingle" />
                  {helper && (
                    <span className="text-gray-600 text-xs">{helper}</span>
                  )}
                </div>
              )}
            />
            <TranslatedFormMessage />
          </FormItem>
        ) : (
          <FileItem
            file={value}
            image={
              video ? (
                <Film className="w-4 h-4 text-gray-500" />
              ) : (
                <img src={URL.createObjectURL(value)} alt="file" />
              )
            }
            onDelete={() => field.onChange(undefined)}
            className={className}
            validationMark={fileItemValidationMark}
          />
        );
      }}
    />
  );
};

export default DragAndDropLoadMedia;
